import React from 'react';
import { Shield } from 'lucide-react';

interface CoverageInfo {
  noClaimsYears: string;
  coverageType: string;
  additionalCover: string[];
  protectNoClaims: boolean;
  paymentFrequency: string;
  hadInsuranceBefore: string;
}

interface CoverageInfoStepProps {
  data: CoverageInfo;
  onInputChange: (field: string, value: string | string[] | boolean) => void;
}

export function CoverageInfoStep({ data, onInputChange }: CoverageInfoStepProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-6">
        <Shield className="w-6 h-6 text-indigo-600" />
        <h2 className="text-xl font-semibold">Coverage Options</h2>
      </div>
      
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Have you ever held car insurance before?
        </label>
        <div className="flex space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Yes"
              checked={data.hadInsuranceBefore === 'Yes'}
              onChange={(e) => onInputChange('hadInsuranceBefore', e.target.value)}
              className="rounded-full h-4 w-4 text-indigo-600"
              required
            />
            <span className="ml-2">Yes</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="No"
              checked={data.hadInsuranceBefore === 'No'}
              onChange={(e) => onInputChange('hadInsuranceBefore', e.target.value)}
              className="rounded-full h-4 w-4 text-indigo-600"
              required
            />
            <span className="ml-2">No</span>
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          How would you like to pay for your insurance?
        </label>
        <div className="flex space-x-4">
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Annual"
              checked={data.paymentFrequency === 'Annual'}
              onChange={(e) => onInputChange('paymentFrequency', e.target.value)}
              className="rounded-full h-4 w-4 text-indigo-600"
              required
            />
            <span className="ml-2">Annual</span>
          </label>
          <label className="inline-flex items-center">
            <input
              type="radio"
              value="Monthly"
              checked={data.paymentFrequency === 'Monthly'}
              onChange={(e) => onInputChange('paymentFrequency', e.target.value)}
              className="rounded-full h-4 w-4 text-indigo-600"
              required
            />
            <span className="ml-2">Monthly</span>
          </label>
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          No Claims Discount (Years)
        </label>
        <select 
          className="w-full p-2 border rounded"
          value={data.noClaimsYears}
          onChange={(e) => onInputChange('noClaimsYears', e.target.value)}
          required
        >
          <option value="">Select years</option>
          <option value="0">0 years</option>
          <option value="1">1 year</option>
          <option value="2">2 years</option>
          <option value="3">3 years</option>
          <option value="4">4 years</option>
          <option value="5">5 years</option>
          <option value="6">6 years</option>
          <option value="7">7 years</option>
          <option value="8">8 years</option>
          <option value="9">9+ years</option>
        </select>
      </div>
      
      {parseInt(data.noClaimsYears) > 0 && (
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="rounded text-indigo-600"
              checked={data.protectNoClaims}
              onChange={(e) => onInputChange('protectNoClaims', e.target.checked)}
            />
            <span>Protect my No Claims Discount</span>
          </label>
          <p className="text-sm text-gray-500 ml-6">
            Protecting your No Claims Discount allows you to have accidents without losing your discount
          </p>
        </div>
      )}

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Coverage Type
        </label>
        <select 
          className="w-full p-2 border rounded"
          value={data.coverageType}
          onChange={(e) => onInputChange('coverageType', e.target.value)}
          required
        >
          <option value="">Select Coverage Type</option>
          <option value="comprehensive">Fully Comprehensive</option>
          <option value="third-party-fire-theft">Third Party Fire & Theft</option>
          <option value="third-party">Third Party Only</option>
        </select>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Additional Cover Options
        </label>
        <div className="space-y-2">
          {['Breakdown Cover', 'Legal Protection', 'Windscreen Cover', 'Courtesy Car'].map((option) => (
            <label key={option} className="flex items-center space-x-2">
              <input
                type="checkbox"
                className="rounded text-indigo-600"
                checked={data.additionalCover.includes(option)}
                onChange={(e) => {
                  const newCover = e.target.checked
                    ? [...data.additionalCover, option]
                    : data.additionalCover.filter(item => item !== option);
                  onInputChange('additionalCover', newCover);
                }}
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}